/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import { withRouter } from "react-router-dom";
import { observer } from "mobx-react";
import makeApi, { URLS } from "Utils/apiURL";
import Utils from "Utils/utils";
import DateUtils from "Utils/DateUtils";
import SequenceUtils from "Utils/SequenceUtils";
import userDetail from "Stores/userDetail";
import scheduleDataStore from "Stores/ScheduleData";
import { sequenceContactData } from "Stores/sequence/Contact";
import { sequenceStoreData } from "Stores/sequenceData";
import { toasterState } from "Components/common/toaster";
import { initialSchedulePopupState } from "./InitialScheduleInfoPopup";
import { requestOpenAddInbox } from "./MultiInboxSelection";
import { approvalPopupState } from "../DeepResearchApproval/ApprovalPopupState";
import { weekDays } from "Static/constant";

/**
 * Status rail for the sequence Configurations tab.
 *
 * A row engine, not bespoke states: every source (inboxes, LinkedIn account,
 * schedule window, approval queue, manual-task backlog, contact funnel,
 * safeguards) emits one row with a severity; rows sort red > amber > green and
 * the header derives from the worst row plus the sequence lifecycle status.
 * All data is either already loaded by this page or fetched from existing
 * endpoints — no new backend.
 */

import { stepVariantTypes } from "./sequenceBlockers";

const MANUAL_STEP_TYPES = ["manualEmail", "linkedin", "phone", "customTask"];

// "HH:mm" -> minutes since midnight
const toMinutes = (time = "") => {
  const [hours = "0", minutes = "0"] = `${time}`.split(":");
  return +hours * 60 + +minutes;
};

const WEEKDAY_KEY = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

// Current weekday key (1=Mon..7=Sun) + minutes-since-midnight in the
// schedule's own timezone. Null when the timezone id is absent or invalid.
const getNowInZone = (timeZone = "") => {
  if (!timeZone) {
    return null;
  }
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    }).formatToParts(new Date());
    const get = type => parts.find(part => part.type === type)?.value;
    const dayKey = WEEKDAY_KEY[get("weekday")];
    const hour = +get("hour") % 24; // "24" at midnight in some engines
    return dayKey
      ? { dayKey, minutes: hour * 60 + +get("minute") }
      : null;
  } catch (e) {
    return null;
  }
};

// Window state for the schedule: open (with closing time) or closed (with the
// next opening). Pure frontend derivation from data already on the page.
const getWindowState = (scheduleWindow = {}) => {
  const ranges = scheduleWindow?.dailyTimeRange || {};
  const hasAnyRange = Object.values(ranges).some(list => list?.length);
  if (!hasAnyRange) {
    return { empty: true };
  }
  const now = getNowInZone(scheduleWindow?.timezone);
  if (!now) {
    return null;
  }
  const todayRanges = ranges[now.dayKey] || [];
  const current = todayRanges.find(
    range =>
      toMinutes(range?.fromTime) <= now.minutes &&
      now.minutes < toMinutes(range?.toTime)
  );
  if (current) {
    return { open: true, closesAt: Utils.getCustomTime(current.toTime) };
  }
  const upcomingToday = todayRanges
    .filter(range => toMinutes(range?.fromTime) > now.minutes)
    .sort((a, b) => toMinutes(a?.fromTime) - toMinutes(b?.fromTime))[0];
  if (upcomingToday) {
    return {
      open: false,
      opensLabel: `today ${Utils.getCustomTime(upcomingToday.fromTime)}`
    };
  }
  for (let offset = 1; offset <= 7; offset++) {
    const dayKey = ((now.dayKey - 1 + offset) % 7) + 1;
    const first = (ranges[dayKey] || [])
      .slice()
      .sort((a, b) => toMinutes(a?.fromTime) - toMinutes(b?.fromTime))[0];
    if (first) {
      return {
        open: false,
        opensLabel: `${offset === 1 ? "tomorrow" : weekDays[dayKey]?.short} ${
          Utils.getCustomTime(first.fromTime) || ""
        }`
      };
    }
  }
  return { empty: true };
};

const SEVERITY_ORDER = { red: 0, amber: 1, green: 2, info: 3 };

// Shimmer placeholder while the settings/throttling data is still loading,
// so the column never pops in empty.
const SequenceStatusRailSkeleton = () => (
  <aside className="sequenceStatusRail railSkeleton">
    <div className="skelTitle linear-background" />
    <div className="skelSub linear-background" />
    {Array.from({ length: 4 }, (value, index) => (
      <div className="skelRow" key={`railSkel${index}`}>
        <span className="skelDot linear-background" />
        <span className="skelLine linear-background" />
      </div>
    ))}
    <div className="skelBtn linear-background" />
  </aside>
);

const RailRow = ({ row = {} }) => (
  <div className={`railRow ${row.severity}`}>
    <span className="railRowMark">
      {row.count != null
        ? row.count
        : row.severity === "red"
        ? "✕"
        : row.severity === "amber"
        ? "!"
        : "✓"}
    </span>
    <div className="railRowBody">
      <div className="railRowTitle">{row.title}</div>
      {row.desc && <div className="railRowDesc">{row.desc}</div>}
      {row.action && (
        <button
          type="button"
          className="railRowAction"
          onClick={row.action.onClick}
        >
          {row.action.label}
        </button>
      )}
    </div>
  </div>
);

const SequenceStatusRail = observer(props => {
  const {
    sequence = {},
    steps = {},
    showSequenceToggleBtnLoad = false,
    checkBoxToggleAction = () => {},
    scheduleWindowId = "",
    getSelectedSeqData = () => {},
    setShowActivateLoader = () => {},
    activatePopupProps = {},
    // Collapsible mode: render as a one-line pulse strip while everything is
    // healthy, auto-expand to the full checklist when something needs the
    // user (red/amber row, draft, paused). Used above the insights card.
    collapsible = false,
    history = {}
  } = props || {};

  const [dueTasksCount, setDueTasksCount] = useState(0);
  // Live day-scoped counters from /cm/seq/{id}/pulse (null until loaded).
  const [pulse, setPulse] = useState(null);
  // null = follow the auto rule; true/false = user's explicit toggle.
  const [collapsedPref, setCollapsedPref] = useState(null);

  const statusKey = (sequence?.status || "").toLowerCase();
  const isDraft = statusKey === "draft";
  const isPaused = statusKey === "paused";

  const stepTypes = useMemo(() => stepVariantTypes(steps), [steps]);
  const hasEmailSteps = stepTypes.has("email") || stepTypes.has("manualEmail");
  const hasLiAutoSteps = stepTypes.has("linkedinAuto");
  const hasManualSteps = MANUAL_STEP_TYPES.some(type => stepTypes.has(type));

  // Funnel — the stats payload this page already loads on mount.
  const funnel = sequenceContactData?.contactsTabCount || {};
  const {
    contactsAdded = 0,
    ongoing = 0,
    completedCampaigns = 0,
    totalReplyCount = 0,
    meetingBookedCount = 0,
    emailsSent,
    paused: pausedContacts = 0,
    awaitingApprovalCount = 0
  } = funnel;
  const funnelLoaded = Object.keys(funnel).length > 0;
  // "Has any contacts" comes from the pulse's cheap Mongo existence count —
  // the Solr-backed stats can lag or return member-filtered zeros on fresh
  // loads. Fallbacks: the sequence doc's counter, then the funnel.
  const totalContactsAdded =
    pulse?.contactsEnrolled != null
      ? +pulse.contactsEnrolled
      : Math.max(+contactsAdded || 0, +sequence?.contactsAdded || 0);

  // Inbox health. Healthy = the live selection observable the Sending
  // Inboxes card maintains — it updates the instant an inbox is added or
  // removed (sequence.inboxIds is a stale snapshot from page load). Broken =
  // assigned-but-revoked, resolved against the global inbox list.
  const healthyInboxes = sequenceStoreData?.selectedConnectedInbox || [];
  const brokenInboxes = useMemo(() => {
    const inboxIds = sequence?.inboxIds || [];
    return (userDetail?.connectedInboxes || []).filter(
      inbox => inboxIds.includes(inbox?.id) && inbox?.status !== "ACTIVE"
    );
  }, [sequence?.inboxIds, userDetail?.connectedInboxes]);
  const assignedInboxes = [...healthyInboxes, ...brokenInboxes];

  const liAccounts = userDetail?.activeLinkedInAccounts || [];

  const scheduleWindow = useMemo(() => {
    const list = scheduleDataStore?.scheduleData?.data || [];
    return (
      list.find(item => item?.id === (scheduleWindowId || sequence?.scheduleWindowId)) ||
      null
    );
  }, [
    scheduleWindowId,
    sequence?.scheduleWindowId,
    scheduleDataStore?.scheduleData
  ]);
  const windowState = scheduleWindow ? getWindowState(scheduleWindow) : null;

  // Manual-task backlog — existing tasks analytics endpoint, per sequence.
  useEffect(() => {
    let mounted = true;
    const fetchDueTasks = async () => {
      if (!sequence?.id || !hasManualSteps) {
        return;
      }
      try {
        const config = {
          url: URLS.getAccountTasksAnalytics,
          method: "POST",
          data: {
            timeZone: Utils.getCurrentUserTimeZone(),
            status: "ACTIVE",
            memberIds: [userDetail?.userInfo?.memberId],
            sequenceIds: [sequence.id],
            dateFilter: { toDate: DateUtils.getTimeStamp(new Date(), "end") }
          }
        };
        const res = await makeApi(config);
        const analytics = res?.data?.analyticsV2 || {};
        mounted && setDueTasksCount(analytics?.dueCount || 0);
      } catch (e) {
        console.error("Status rail: due tasks fetch failed", e);
      }
    };
    fetchDueTasks();
    return () => {
      mounted = false;
    };
  }, [sequence?.id, hasManualSteps]);

  useEffect(() => {
    let mounted = true;
    const fetchPulse = async () => {
      if (!sequence?.id || isDraft) {
        return;
      }
      try {
        const config = {
          url: URLS.getSequencePulse.replace("<<seqID>>", sequence.id),
          method: "GET"
        };
        const res = await makeApi(config);
        if (mounted && res?.data && typeof res.data === "object") {
          setPulse(res.data);
        }
      } catch (e) {
        console.error("Status rail: pulse fetch failed", e);
      }
    };
    fetchPulse();
    return () => {
      mounted = false;
    };
  }, [sequence?.id, isDraft]);

  const goTo = path => () => {
    if (path?.startsWith("#")) {
      // Hash targets write to the address bar directly so the tab state
      // survives refresh (the router picks it up via popstate).
      window.location.hash = path;
    } else if (history?.push) {
      history.push(path);
    }
  };

  // Synthetic checkbox event for the shared toggle flow — must satisfy
  // Utils.preventDefaultFn (persist/preventDefault/stopPropagation) or the
  // handler throws before the pause confirmation can show.
  const toggleSequence = checked => () => {
    checkBoxToggleAction({
      persist: () => {},
      preventDefault: () => {},
      stopPropagation: () => {},
      target: { checked }
    });
  };

  // Draft activation — same flow as the old banner's ActivateToggle.
  const activateDraft = () => {
    if (SequenceUtils.checkEmailSteps(sequence) && !Utils.isAccountConnected()) {
      Utils.showAddAccountPopup(
        props,
        "Please connect your email account before activating the sequences"
      );
    } else if (!SequenceUtils.hasSteps(sequence)) {
      toasterState.setToastMsg(
        "Please add at least one step to start the sequence.",
        "fail"
      );
    } else {
      initialSchedulePopupState.setDefaultValues(sequence);
      initialSchedulePopupState.setDefaultProps({
        ...activatePopupProps,
        sequence,
        sequenceId: sequence?.id
      });
      initialSchedulePopupState.setShowPopup(true);
      initialSchedulePopupState.setCbk(() => {
        setShowActivateLoader(true);
        sequenceStoreData.setSeqActivated(false);
        getSelectedSeqData();
      });
    }
  };

  // Backend timezone labels are long — "(GMT-8:00) Pacific Standard Time -
  // Pacific". Keep just the readable middle for the rail.
  const tzLabel = (scheduleWindow?.timezoneName || "")
    .replace(/^\(GMT[^)]*\)\s*/, "")
    .split(" - ")[0];

  // ── Row engine ──
  const rows = [];

  if (isDraft && !SequenceUtils.hasSteps(sequence)) {
    rows.push({
      severity: "red",
      title: "Add at least one step",
      desc: "The sequence has nothing to send yet.",
      action: { label: "Add a step", onClick: goTo("#overview") }
    });
  }

  // Workspace-level inbox presence decides the CTA: pick from the existing
  // list right here, or go connect a first one in settings.
  const hasWorkspaceInboxes = (userDetail?.connectedInboxes || []).some(
    inbox => inbox?.status === "ACTIVE"
  );

  if (hasEmailSteps) {
    if (!assignedInboxes.length) {
      rows.push({
        severity: "red",
        title: hasWorkspaceInboxes
          ? "Add a sending inbox"
          : "Connect a sending inbox",
        desc: "This sequence has email steps. Emails cannot send without an inbox.",
        action: hasWorkspaceInboxes
          ? { label: "Add inbox", onClick: () => requestOpenAddInbox() }
          : { label: "Connect inbox", onClick: goTo("/settings/account") }
      });
    } else if (!healthyInboxes.length) {
      rows.push({
        severity: "red",
        title:
          assignedInboxes.length > 1
            ? "All inboxes disconnected"
            : "Inbox disconnected",
        desc: `${brokenInboxes[0]?.email || ""}${
          brokenInboxes.length > 1 ? ` +${brokenInboxes.length - 1} more` : ""
        } lost connection. Email sending is stopped.`,
        action: { label: "Reconnect", onClick: goTo("/settings/account") }
      });
    } else if (brokenInboxes.length) {
      rows.push({
        severity: "amber",
        title: `${brokenInboxes.length} of ${assignedInboxes.length} inboxes disconnected`,
        desc: `Contacts assigned to ${brokenInboxes
          .map(inbox => inbox?.email)
          .join(", ")} are on hold. ${
          sequence?.reassignInbox
            ? "Auto-reassign is on — new sends move to healthy inboxes."
            : "Turn on auto-reassign in Sending inboxes to move them."
        }`,
        action: { label: "Reconnect", onClick: goTo("/settings/account") }
      });
    } else {
      const usageById = {};
      (pulse?.inboxUsage || []).forEach(item => {
        if (item?.inboxId) {
          usageById[item.inboxId] = item;
        }
      });
      const healthyUsage = healthyInboxes
        .map(inbox => usageById[inbox?.id])
        .filter(Boolean);
      const totalSentToday = healthyUsage.reduce(
        (sum, item) => sum + (+item.sentToday || 0),
        0
      );
      const limitsKnown =
        healthyUsage.length > 0 && healthyUsage.every(item => +item.dailyLimit > 0);
      if (limitsKnown && healthyUsage.every(item => (+item.sentToday || 0) >= +item.dailyLimit)) {
        rows.push({
          severity: "amber",
          title: "Daily inbox limit reached",
          desc: `${totalSentToday} sent today. Sending resumes tomorrow — raise the limit or add another inbox.`
        });
      }
      const usageSuffix =
        healthyUsage.length === 1 && +healthyUsage[0].dailyLimit > 0
          ? ` · ${+healthyUsage[0].sentToday || 0}/${healthyUsage[0].dailyLimit} today`
          : healthyUsage.length > 1 && totalSentToday > 0
          ? ` · ${totalSentToday} sent today`
          : "";
      rows.push({
        severity: "green",
        title: "Inboxes healthy",
        desc: `${healthyInboxes.length} connected${
          healthyInboxes.length > 1 ? " · contacts split evenly" : ""
        }${usageSuffix}`
      });
    }
  }

  if (hasLiAutoSteps) {
    rows.push(
      liAccounts.length
        ? {
            severity: "green",
            title: "LinkedIn connected",
            desc: "Automatic LinkedIn steps can run"
          }
        : {
            severity: "red",
            title: "LinkedIn disconnected",
            desc: "Automatic LinkedIn steps can't run until you reconnect. Contacts wait at their LinkedIn step.",
            action: {
              label: "Reconnect LinkedIn",
              onClick: goTo("/settings/account/linkedin")
            }
          }
    );
  }

  if (scheduleWindow) {
    if (windowState?.empty) {
      rows.push({
        severity: "red",
        title: "No sending window set",
        desc: `"${scheduleWindow?.name}" has no hours on any day — nothing can send.`
      });
    } else if (windowState?.open) {
      rows.push({
        severity: "green",
        title: "Sending window open",
        desc: `Closes ${windowState.closesAt}${tzLabel ? ` · ${tzLabel}` : ""}`
      });
    } else if (windowState) {
      rows.push({
        severity: "info",
        title: "Outside sending hours",
        desc: `Opens ${windowState.opensLabel}${tzLabel ? ` · ${tzLabel}` : ""}`
      });
    }
  }

  // "What's about to go out" — today/tomorrow queue split by channel.
  const queueDayLabel = (day = {}) => {
    const emailCount = +day?.email || 0;
    const liCount = +day?.linkedin || 0;
    const parts = [];
    if (emailCount) {
      parts.push(`${emailCount} email${emailCount > 1 ? "s" : ""}`);
    }
    if (liCount) {
      parts.push(`${liCount} LinkedIn`);
    }
    return parts.join(" · ");
  };
  const queueToday = queueDayLabel(pulse?.queue?.today);
  const queueTomorrow = queueDayLabel(pulse?.queue?.tomorrow);
  // Active sequences only — a draft or paused sequence sends nothing, so a
  // "sending queue" there would be a lie.
  if (!isDraft && !isPaused && (queueToday || queueTomorrow)) {
    rows.push({
      severity: "info",
      title: "Sending queue",
      desc: [
        queueToday && `Today: ${queueToday}`,
        queueTomorrow && `Tomorrow: ${queueTomorrow}`
      ]
        .filter(Boolean)
        .join(" — ")
    });
  }

  if (awaitingApprovalCount > 0) {
    rows.push({
      severity: "amber",
      count: awaitingApprovalCount,
      title: `${awaitingApprovalCount} contact${
        awaitingApprovalCount > 1 ? "s" : ""
      } awaiting your approval`,
      desc: "Their steps hold until the drafts are approved.",
      action: {
        label: "Review drafts",
        onClick: () => approvalPopupState.open(sequence?.id)
      }
    });
  }

  if (dueTasksCount > 0) {
    rows.push({
      severity: "amber",
      count: dueTasksCount,
      title: `${dueTasksCount} manual task${
        dueTasksCount > 1 ? "s" : ""
      } waiting on you`,
      desc: "LinkedIn, call and manual email steps need you to complete them.",
      action: { label: "Go to tasks", onClick: goTo("/tasks") }
    });
  }

  const everyoneFinished =
    !isDraft &&
    funnelLoaded &&
    totalContactsAdded > 0 &&
    contactsAdded > 0 &&
    ongoing === 0 &&
    completedCampaigns > 0;

  if (!isDraft) {
    if (totalContactsAdded === 0) {
      rows.push({
        severity: "amber",
        title: "No contacts yet",
        desc: "Add contacts to start sending."
      });
    } else if (everyoneFinished) {
      rows.push({
        severity: "green",
        title: "Every contact completed every step",
        desc: "Add a step, then reactivate finished contacts from the Contacts tab — they continue from where they stopped."
      });
    } else if (isPaused && pausedContacts > 0) {
      // Pausing flips contacts to PAUSED, so "ongoing" reads zero — show
      // what Resume actually picks up instead of an all-zero funnel.
      rows.push({
        severity: "info",
        title: `${pausedContacts} contact${
          pausedContacts > 1 ? "s" : ""
        } mid-sequence`,
        desc: "They hold their place and continue from their next step on resume."
      });
    } else if (
      ongoing > 0 ||
      completedCampaigns > 0 ||
      totalReplyCount > 0
    ) {
      rows.push({
        severity: "green",
        title: "Contacts",
        desc: `${ongoing} active · ${completedCampaigns} finished · ${totalReplyCount} replied`
      });
    }
    // All zeros with an approval queue: the awaiting-approval row already
    // tells the story — a green all-zero row would just contradict it.
  }

  // Same feature-flag gating as the settings card so both show "X of N on".
  const { calendarEnabled = false } = userDetail?.userInfo || {};
  const { calendarIntegrationEnabled = false, liAutomationEnabled = false } =
    userDetail?.userFeatureFlag || {};
  const shownSafeguards = [
    { on: sequence?.stopEmailOnReplyReceived, show: true },
    { on: sequence?.stopEmailOnLinkedInReplyReceived, show: liAutomationEnabled },
    {
      on: sequence?.stopEmailOnMeetingBooked,
      show: calendarEnabled && calendarIntegrationEnabled
    },
    { on: sequence?.excludeContactEnabled, show: true },
    { on: sequence?.excludeActiveContactEnabled, show: true },
    { on: sequence?.emailValidationEnabled, show: true }
  ].filter(item => item.show);
  const safeguardsOn = shownSafeguards.filter(item => item.on).length;
  rows.push(
    safeguardsOn
      ? {
          severity: "green",
          title: "Safeguards on",
          desc: `${safeguardsOn} of ${shownSafeguards.length} on${
            sequence?.stopEmailOnReplyReceived
              ? totalReplyCount > 0
                ? ` · stops on reply · ${totalReplyCount} stopped so far`
                : " · stops on reply"
              : ""
          }`
        }
      : {
          severity: "amber",
          title: "No safeguards on",
          desc: "Sending continues even after a contact replies."
        }
  );

  rows.sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
  );

  const worstSeverity = rows[0]?.severity || "green";
  const firstProblem = rows.find(row =>
    ["red", "amber"].includes(row.severity)
  );

  // ── Header: lifecycle status first, then the worst row ──
  let header;
  if (isDraft) {
    header = {
      cls: "draft",
      title: "Ready to activate?",
      sub: "Everything this sequence needs before it starts sending."
    };
  } else if (isPaused) {
    header = {
      cls: "paused",
      title: "Sequence paused",
      sub:
        worstSeverity === "red"
          ? `Fix before resuming: ${firstProblem?.title || ""}`
          : "Nothing is being sent or scheduled."
    };
  } else if (worstSeverity === "red") {
    header = {
      cls: "bad",
      title: "Needs attention",
      sub: firstProblem?.title || ""
    };
  } else if (everyoneFinished) {
    header = {
      cls: "done",
      title: "All contacts finished",
      sub: "Nothing left to send — the sequence stays live for new contacts."
    };
  } else if (worstSeverity === "amber") {
    header = {
      cls: "warn",
      title:
        totalContactsAdded === 0 ? "Ready and waiting" : "Sending — with issues",
      sub: firstProblem?.title || ""
    };
  } else if (windowState?.open) {
    header = {
      cls: "live",
      title: "Sending live",
      sub: `Window open · closes ${windowState.closesAt}`
    };
  } else {
    header = {
      cls: "quiet",
      title: "Quiet hours",
      sub: windowState?.opensLabel
        ? `Sending resumes ${windowState.opensLabel}`
        : "Outside the sending window."
    };
  }

  // A strip of zeros is noise — show stats only once there's a real number.
  const showStats =
    !isDraft &&
    totalContactsAdded > 0 &&
    ((emailsSent || 0) > 0 || totalReplyCount > 0 || meetingBookedCount > 0);

  // Collapsed only when nothing needs the user: active sequence, no red or
  // amber row, no draft/paused footer action hidden from view.
  const autoCollapsed =
    !isDraft && !isPaused && !["red", "amber"].includes(worstSeverity);
  const collapsed =
    collapsible && (collapsedPref === null ? autoCollapsed : collapsedPref);

  if (collapsed) {
    return (
      <aside
        className={`sequenceStatusRail collapsedStrip ${header.cls}`}
        onClick={() => setCollapsedPref(false)}
      >
        <span className={`railStatusDot ${header.cls}`} />
        <span className="stripTitle">{header.title}</span>
        {showStats && (
          <span className="stripStats">
            {emailsSent != null ? `${emailsSent} sent · ` : ""}
            {totalReplyCount} repl{totalReplyCount === 1 ? "y" : "ies"}
          </span>
        )}
        <i className="material-icons stripChevron">expand_more</i>
      </aside>
    );
  }

  return (
    <aside className={`sequenceStatusRail ${header.cls}`}>
      <div className="railHeader">
        <span className={`railStatusDot ${header.cls}`} />
        <div className="railHeaderText">
          <div className="railTitle">{header.title}</div>
          {header.sub && <div className="railSub">{header.sub}</div>}
        </div>
        {collapsible && (
          <i
            className="material-icons railCollapse"
            onClick={() => setCollapsedPref(true)}
            title="Collapse"
          >
            expand_less
          </i>
        )}
      </div>
      {showStats && (
        <div className="railStats">
          {!isPaused && pulse?.emailsSentToday > 0 && (
            <div className="railStat">
              <span className="railStatNum">{pulse.emailsSentToday}</span>
              <span className="railStatLbl">sent today</span>
            </div>
          )}
          {emailsSent != null && (
            <div className="railStat">
              <span className="railStatNum">{emailsSent}</span>
              <span className="railStatLbl">emails sent</span>
            </div>
          )}
          <div className="railStat">
            <span className="railStatNum">{totalReplyCount}</span>
            <span className="railStatLbl">replies</span>
          </div>
          {meetingBookedCount > 0 && (
            <div className="railStat">
              <span className="railStatNum">{meetingBookedCount}</span>
              <span className="railStatLbl">meetings</span>
            </div>
          )}
        </div>
      )}
      <div className="railRows">
        {rows.map((row, index) => (
          <RailRow key={`railRow${index}`} row={row} />
        ))}
      </div>
      {isDraft && (
        <div className="railFooter">
          <button
            type="button"
            className={`railPrimaryBtn ${
              showSequenceToggleBtnLoad ? "loading" : ""
            }`}
            disabled={showSequenceToggleBtnLoad}
            onClick={activateDraft}
          >
            Activate sequence
          </button>
        </div>
      )}
      {!isDraft && (
        <div className="railFooter">
          {isPaused ? (
            <button
              type="button"
              className={`railPrimaryBtn ${
                showSequenceToggleBtnLoad ? "loading" : ""
              }`}
              disabled={showSequenceToggleBtnLoad}
              onClick={toggleSequence(true)}
            >
              Resume sequence
            </button>
          ) : (
            <button
              type="button"
              className={`railSecondaryBtn ${
                showSequenceToggleBtnLoad ? "loading" : ""
              }`}
              disabled={showSequenceToggleBtnLoad}
              onClick={toggleSequence(false)}
            >
              Pause sequence
            </button>
          )}
          {isPaused && windowState && !windowState.empty && (
            <div className="railFooterHint">
              {windowState.open
                ? "Sends restart right away — the window is open now"
                : `Sends restart ${windowState.opensLabel || "in the next open window"}`}
            </div>
          )}
        </div>
      )}
    </aside>
  );
});

export default withRouter(SequenceStatusRail);
export { SequenceStatusRail, SequenceStatusRailSkeleton };
